import { useCallback, useMemo } from 'react'
import DataKeys from 'k8s/DataKeys'
import useServerSidePagination, { FetchActionParams } from 'core/hooks/useServerSidePagination'
import { getVolumesCount, listVolumesNextPage, pollVolumes } from './actions'
import { volumesSelector } from './selectors'

interface UseVolumesPaginationConfig {
  allTenants?: boolean
  /** Default sort key for initial load (default: 'created_at') */
  defaultSortKey?: string
  /** Default sort direction for initial load (default: 'desc') */
  defaultSortDir?: 'asc' | 'desc'
}

/**
 * Volumes-specific pagination hook.
 *
 * This is a thin wrapper around the generic useServerSidePagination hook
 * that provides volumes-specific configuration.
 */
export function useVolumesPagination({
  allTenants = false,
  defaultSortKey = 'created_at',
  defaultSortDir = 'desc',
}: UseVolumesPaginationConfig) {
  // Memoize fetch action to prevent unnecessary re-renders
  const fetchAction = useCallback(
    async (params: FetchActionParams) => {
      // Call the action - it returns the fetched volumes for THIS page only
      const volumes = await listVolumesNextPage.call({
        allTenants,
        limit: params.limit,
        marker: params.marker || undefined,
        sort_key: params.sortKey,
        sort_dir: params.sortDir,
        search_field: params.searchField,
        search_value: params.searchValue,
        filters: params.filters,
      })

      // Return the fetched data (current page only) and next marker
      const lastItem = volumes?.[volumes.length - 1]
      return {
        data: volumes || [],
        nextMarker: lastItem?.id,
      }
    },
    [allTenants],
  )

  // Memoize count action - accepts params for filtered counts
  const countAction = useCallback(
    (params?: { searchField?: string; searchValue?: string; filters?: Record<string, string> }) =>
      getVolumesCount(allTenants, params),
    [allTenants],
  )

  // Memoize poll action - uses the same params as fetch action
  const pollAction = useCallback(
    async (params: FetchActionParams) => {
      const volumes = await pollVolumes.call({
        allTenants,
        limit: params.limit,
        marker: params.marker || undefined,
        sort_key: params.sortKey,
        sort_dir: params.sortDir,
        search_field: params.searchField,
        search_value: params.searchValue,
        filters: params.filters,
      })
      return volumes || []
    },
    [allTenants],
  )

  // Memoize dependencies array to prevent effect re-runs
  const dependencies = useMemo(() => [allTenants], [allTenants])

  // Use the generic pagination hook with volumes-specific config
  const paginationResult = useServerSidePagination({
    dataKey: DataKeys.OpenstackVolumes,
    dataSelector: volumesSelector,
    fetchAction,
    countAction,
    pollAction,
    uniqueIdentifier: 'id',
    entityKey: `OpenstackVolumes`,
    dependencies,
    defaultSortKey,
    defaultSortDir,
  })

  return paginationResult
}

export default useVolumesPagination
